import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { ReleaseState } from '@internal/backstage-plugin-release-common';
import { errorMessage } from './useReleaseRead';

export type ReleaseStatesState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; error: Error }
  | { kind: 'loaded'; byService: Record<string, ReleaseState> };

/**
 * Each service repository's tags and open release line, used to derive the
 * next step rather than asking anybody to type a version.
 *
 * Takes a list because promotion is per component: there is no fleet version,
 * and production currently holds five different release lines at once. Each
 * repository is asked separately and each computes its own next version.
 *
 * Pass an empty list to stay idle — the dialog only asks once it is open.
 */
export function useReleaseStates(
  services: string[],
  generation = 0,
): ReleaseStatesState {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<ReleaseStatesState>({ kind: 'idle' });

  // Effect dependencies must be stable across renders, and the caller builds a
  // fresh array every time.
  const key = services.join(',');

  useEffect(() => {
    if (key === '') {
      setState({ kind: 'idle' });
      return undefined;
    }

    let cancelled = false;
    setState({ kind: 'loading' });

    const load = async () => {
      const baseUrl = await discoveryApi.getBaseUrl('release');
      const names = key.split(',');
      const entries = await Promise.all(
        names.map(async service => {
          const response = await fetchApi.fetch(
            `${baseUrl}/release-state?service=${encodeURIComponent(service)}${
              generation > 0 ? '&refresh=true' : ''
            }`,
          );
          if (!response.ok) {
            throw new Error(
              `${service}: ${await errorMessage(response)}`,
            );
          }
          const body = (await response.json()) as Partial<ReleaseState>;
          const release: ReleaseState = {
            tags: Array.isArray(body.tags)
              ? body.tags.filter((tag): tag is string => typeof tag === 'string')
              : [],
            line: body.line ?? null,
            pending: Array.isArray(body.pending) ? body.pending : [],
          };
          return [service, release] as const;
        }),
      );
      if (!cancelled) {
        setState({
          kind: 'loaded',
          byService: Object.fromEntries(entries),
        });
      }
    };

    load().catch(e => {
      if (!cancelled) {
        setState({ kind: 'error', error: e as Error });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, key, generation]);

  return state;
}
